// MobiRoute Vehicles Management

import React, { useState, useEffect } from 'react';
import { Button as DxButton } from 'devextreme-react/button';
import { Popup } from 'devextreme-react/popup';
import { LoadPanel } from 'devextreme-react/load-panel';
import { SelectBox } from 'devextreme-react/select-box';
import { TextBox } from 'devextreme-react/text-box';
import { NumberBox } from 'devextreme-react/number-box';
import CustomDataGrid from '../Customer/CustomDataGrid';


interface Vehicle {
  id: string;
  name: string;
  capacity: number;
  costPerKm: number;
  maxDistance: number;
  createdAt: string;
  isActive: boolean;
  type: string;
  description: string;
}

interface VehicleType {
  name: string;
  icon: string;
  color: string;
}

interface VehiclesData {
  vehicles: Vehicle[];
  vehicleTypes: Record<string, VehicleType>;
}

export const Vehicles: React.FC = () => {
  const [vehicles, setVehicles] = useState<Vehicle[]>([]);
  const [vehicleTypes, setVehicleTypes] = useState<Record<string, VehicleType>>({});
  const [loading, setLoading] = useState(false);
  const [vehicleDialogVisible, setVehicleDialogVisible] = useState(false);
  const [editingVehicle, setEditingVehicle] = useState<Vehicle | null>(null);
  const [newVehicle, setNewVehicle] = useState<Partial<Vehicle>>({
    name: '',
    capacity: 0,
    costPerKm: 0,
    maxDistance: 0,
    isActive: true,
    type: 'van',
    description: ''
  });

  // Load vehicles from JSON file only
  const loadVehicles = async () => {
    setLoading(true);
    try {
      const response = await fetch('/vehicles.json');
      if (!response.ok) {
        throw new Error('Failed to fetch vehicles data');
      }
      
      const data: VehiclesData = await response.json();
      setVehicles(data.vehicles);
      setVehicleTypes(data.vehicleTypes);
      
      console.log(`${data.vehicles.length} araç JSON'dan yüklendi`);
    } catch (error) {
      console.error('Error loading vehicles:', error);
      alert('Araç verileri yüklenirken hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  // Update vehicles state only (no localStorage)
  const updateVehiclesState = (vehiclesToUpdate: Vehicle[]) => {
    setVehicles(vehiclesToUpdate);
  };

  // Load vehicles on component mount
  useEffect(() => {
    loadVehicles();
  }, []);

  // Generate unique ID
  const generateId = () => {
    return `vehicle_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  };

  // Add new vehicle
  const addVehicle = () => {
    if (!newVehicle.name || !newVehicle.capacity || !newVehicle.costPerKm || !newVehicle.maxDistance) {
      alert('Lütfen tüm gerekli alanları doldurun');
      return;
    }

    const vehicle: Vehicle = {
      id: generateId(),
      name: newVehicle.name || '',
      capacity: newVehicle.capacity || 0,
      costPerKm: newVehicle.costPerKm || 0,
      maxDistance: newVehicle.maxDistance || 0,
      createdAt: new Date().toISOString(),
      isActive: newVehicle.isActive || true,
      type: newVehicle.type || 'van',
      description: newVehicle.description || ''
    };

    const updatedVehicles = [...vehicles, vehicle];
    updateVehiclesState(updatedVehicles);

    alert('Araç başarıyla eklendi');

    setNewVehicle({
      name: '',
      capacity: 0,
      costPerKm: 0,
      maxDistance: 0,
      isActive: true,
      type: 'van',
      description: ''
    });
    setVehicleDialogVisible(false);
  };

  // Edit vehicle
  const editVehicle = (vehicle: Vehicle) => {
    setEditingVehicle(vehicle);
    setNewVehicle(vehicle);
    setVehicleDialogVisible(true);
  };

  // Update vehicle
  const updateVehicle = () => {
    if (!editingVehicle || !newVehicle.name || !newVehicle.capacity || !newVehicle.costPerKm || !newVehicle.maxDistance) {
      alert('Lütfen tüm gerekli alanları doldurun');
      return;
    }

    const updatedVehicles = vehicles.map(v =>
      v.id === editingVehicle.id
        ? {
            ...v,
            name: newVehicle.name || '',
            capacity: newVehicle.capacity || 0,
            costPerKm: newVehicle.costPerKm || 0,
            maxDistance: newVehicle.maxDistance || 0,
            isActive: newVehicle.isActive || true,
            type: newVehicle.type || 'van',
            description: newVehicle.description || ''
          }
        : v
    );

    updateVehiclesState(updatedVehicles);

    alert('Araç başarıyla güncellendi');

    setEditingVehicle(null);
    setNewVehicle({
      name: '',
      capacity: 0,
      costPerKm: 0,
      maxDistance: 0,
      isActive: true,
      type: 'van',
      description: ''
    });
    setVehicleDialogVisible(false);
  };

  // Grid columns configuration
  const columns = [
    {
      dataField: 'isActive',
      caption: 'Durum',
      width: 140,
      alignment: 'left' as const,
      cellRender: (data: any) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <div style={{
            width: '12px',
            height: '12px',
            borderRadius: '50%',
            backgroundColor: data.value ? '#10b981' : '#ef4444',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            <i className={`pi ${data.value ? 'pi-check' : 'pi-times'}`} style={{ color: 'white', fontSize: '8px' }}></i>
          </div>
          <span style={{ fontSize: '12px', fontWeight: '500', color: data.value ? '#10b981' : '#ef4444' }}>
            {data.value ? 'Aktif' : 'Pasif'}
          </span>
        </div>
      )
    },
    {
      dataField: 'name',
      caption: 'Araç Adı',
      dataType: 'string' as const,
      width: 200,
      cellRender: (data: any) => (
        <div style={{ fontWeight: '600', color: '#1e293b' }}>
          {data.value || 'N/A'}
        </div>
      )
    },
    {
      dataField: 'type',
      caption: 'Tip',
      dataType: 'string' as const,
      width: 150,
      cellRender: (data: any) => {
        const vehicleType = vehicleTypes[data.value];
        return (
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <i
              className={vehicleType?.icon || 'pi pi-car'}
              style={{ color: vehicleType?.color || '#3b82f6', fontSize: '16px' }}
            ></i>
            <span style={{
              backgroundColor: '#e3f2fd',
              color: '#1976d2',
              padding: '4px 8px',
              borderRadius: '4px',
              fontSize: '12px',
              fontWeight: '500'
            }}>
              {vehicleType?.name || data.value}
            </span>
          </div>
        );
      }
    },
    {
      dataField: 'capacity',
      caption: 'Kapasite',
      dataType: 'number' as const,
      width: 140,
      alignment: 'center' as const,
      cellRender: (data: any) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', justifyContent: 'center' }}>
          <i className="pi pi-box" style={{ color: '#8b5cf6', fontSize: '14px' }}></i>
          <span style={{
            backgroundColor: '#f3f4f6',
            color: '#374151',
            padding: '4px 8px',
            borderRadius: '4px',
            fontSize: '12px',
            fontWeight: '600'
          }}>
            {data.value} kg
          </span>
        </div>
      )
    },
    {
      dataField: 'costPerKm',
      caption: 'Maliyet/km',
      dataType: 'number' as const,
      width: 160,
      alignment: 'center' as const,
      cellRender: (data: any) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', justifyContent: 'center' }}>
          <i className="pi pi-euro" style={{ color: '#f59e0b', fontSize: '14px' }}></i>
          <span style={{
            backgroundColor: '#fef3c7',
            color: '#92400e',
            padding: '4px 8px',
            borderRadius: '4px',
            fontSize: '12px',
            fontWeight: '500'
          }}>
            €{data.value}/km
          </span>
        </div>
      )
    },
    {
      dataField: 'maxDistance',
      caption: 'Maks. Mesafe',
      dataType: 'number' as const,
      width: 160,
      alignment: 'center' as const,
      cellRender: (data: any) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', justifyContent: 'center' }}>
          <i className="pi pi-map" style={{ color: '#f97316', fontSize: '14px' }}></i>
          <span style={{
            backgroundColor: '#fed7aa',
            color: '#9a3412',
            padding: '4px 8px',
            borderRadius: '4px',
            fontSize: '12px',
            fontWeight: '500'
          }}>
            {data.value} km
          </span>
        </div>
      )
    }
  ];

  const handleAddNew = () => {
    setEditingVehicle(null);
    setNewVehicle({
      name: '',
      capacity: 0,
      costPerKm: 0,
      maxDistance: 0,
      isActive: true,
      type: 'van',
      description: ''
    });
    setVehicleDialogVisible(true);
  };

  return (
    <div className="view crm-contact-list">
      <div className="view-wrapper view-wrapper-contact-list">
        {loading ? (
          <LoadPanel
            visible={true}
            showIndicator={true}
            showPane={true}
            message="Araçlar yükleniyor..."
          />
        ) : (
          <CustomDataGrid
            data={vehicles}
            columns={columns}
            keyExpr="id"
            onRefresh={loadVehicles}
            onAddNew={handleAddNew}
          />
        )}
      </div>

      {/* Add/Edit Vehicle Dialog */}
      <Popup
        visible={vehicleDialogVisible}
        onHiding={() => {
          setVehicleDialogVisible(false);
          setEditingVehicle(null);
          setNewVehicle({
            name: '',
            capacity: 0,
            costPerKm: 0,
            maxDistance: 0,
            isActive: true,
            type: 'van',
            description: ''
          });
        }}
        dragEnabled={false}
        hideOnOutsideClick={true}
        showTitle={true}
        title={editingVehicle ? "Araç Düzenle" : "Yeni Araç Ekle"}
        width={500}
        height="auto"
      >
        <div style={{ padding: '20px' }}>
          <div style={{ marginBottom: '20px' }}>
            <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600' }}>Araç Adı *</label>
            <TextBox
              value={newVehicle.name}
              onValueChanged={(e) => setNewVehicle({ ...newVehicle, name: e.value })}
              placeholder="Araç adını girin"
            />
          </div>

          <div style={{ marginBottom: '20px' }}>
            <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600' }}>Araç Tipi *</label>
            <SelectBox
              value={newVehicle.type}
              onValueChanged={(e) => setNewVehicle({ ...newVehicle, type: e.value })}
              dataSource={Object.entries(vehicleTypes).map(([key, type]) => ({
                text: type.name,
                value: key
              }))}
              displayExpr="text"
              valueExpr="value"
              placeholder="Araç tipini seçin"
            />
          </div>

          <div style={{ marginBottom: '20px' }}>
            <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600' }}>Kapasite (kg) *</label>
            <NumberBox
              value={newVehicle.capacity}
              onValueChanged={(e) => setNewVehicle({ ...newVehicle, capacity: e.value || 0 })}
              placeholder="Kapasiteyi kg cinsinden girin"
              min={0}
            />
          </div>

          <div style={{ marginBottom: '20px' }}>
            <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600' }}>Kilometre Maliyeti (€) *</label>
            <NumberBox
              value={newVehicle.costPerKm}
              onValueChanged={(e) => setNewVehicle({ ...newVehicle, costPerKm: e.value || 0 })}
              placeholder="Kilometre maliyetini girin"
              min={0}
              format="currency"
            />
          </div>

          <div style={{ marginBottom: '20px' }}>
            <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600' }}>Maksimum Mesafe (km) *</label>
            <NumberBox
              value={newVehicle.maxDistance}
              onValueChanged={(e) => setNewVehicle({ ...newVehicle, maxDistance: e.value || 0 })}
              placeholder="Maksimum mesafeyi girin"
              min={0}
            />
          </div>

          <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
            <DxButton
              text="İptal"
              icon="close"
              type="normal"
              onClick={() => {
                setVehicleDialogVisible(false);
                setEditingVehicle(null);
                setNewVehicle({
                  name: '',
                  capacity: 0,
                  costPerKm: 0,
                  maxDistance: 0,
                  isActive: true,
                  type: 'van',
                  description: ''
                });
              }}
            />
            <DxButton
              text={editingVehicle ? "Güncelle" : "Ekle"}
              icon={editingVehicle ? "check" : "plus"}
              type="default"
              onClick={editingVehicle ? updateVehicle : addVehicle}
            />
          </div>
        </div>
      </Popup>
    </div>
  );
};

export default Vehicles;
